import { useEffect, useRef, useState } from 'react';
import { Dispatch, RootState } from '../../store/store';
import { connect } from 'react-redux';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import CycleSettings from '../../enums/cycle-setting.enum';
import { CycleSettingAction } from '../../enums/action';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { stepsLimitCount } from '../../pages/Recipes/constants';
import { IRecipeEditable, IRecipeStepEditable, StepMode } from '../../types/recipes-types';
import { convertRecipeToPostWeldSettings } from './utils';
import { ChooseRecipe } from './components/ChooseRecipeModal';
import { RecipeUpdateFields } from './components/RecipeUpdateFields';
import { ManagedStepsTable } from './components/ManagedStepsTable';
import { ElementSize } from '../../types/element-size';
import { useTranslation } from 'react-i18next';
import { parseMinutes } from '../../utils/minutes.parser';
import { ValidationError } from 'yup';
import CycleMeasurement from '../../enums/cycle-measurement';
import { IPostWeldRecipeStep, IPostWeldSettings } from '../../types/cycle-settings-types';
import { DeviceStepsTable } from './components/DeviceStepsTable';
import getMeasurementPreference from '../../utils/getMeasurementPreference';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../utils/temperature-unit-conversion';
import { createPostWeldRecipeValidationSchema } from '../../utils/recipe-unit-validation';
import { IDbCycleSettings } from '../../store/models/cycles';
import UnitMismatchWarning, {
  getControllerMeasurement,
  hasMixedContractChannelUnits,
  hasMixedContractControllerUnits as getHasMixedContractControllerUnits,
} from '../CycleSettings/components/UnitMismatchWarning';

type Props = { cycleId: string };
type StateProps = ReturnType<typeof mapState>;
type DispatchProps = ReturnType<typeof mapDispatch>;
type ComponentProps = StateProps & DispatchProps & Props;

export interface IOnUpdateSettingField {
  action: CycleSettingAction;
  key?: string;
  value?: string;
}

const swapItems = (first: IRecipeStepEditable, scnd: IRecipeStepEditable) => {
  return { first: { ...first, order: scnd.order }, scnd: { ...scnd, order: first.order } };
};

const getStepMeasurement = (step: IRecipeStepEditable): CycleMeasurement =>
  step.units || (step.isCelsius === false ? CycleMeasurement.Fahrenheit : CycleMeasurement.Celsius);

const PostWeldSettingsView = ({
  cycleId,
  updateSettings,
  clearSettings,
  getDbRecipe,
  getDbRecipeSteps,
  createRecipeFromScratch,
  cycleDbSettings,
  updateDbSettings,
  settings,
  measurement,
  controllerMeasurement,
  hasMixedContractControllerUnits,
}: ComponentProps) => {
  const [deviceStepsTop, setDeviceStepsTop] = useState<number>(200);
  const [deviceStepsWidth, setDeviceStepsWidth] = useState<number>(0);

  useEffect(() => {
    // DeviceStepsTable is absolutely positioned against the shared
    // .c-cycle-settings ancestor (it needs to render below ChannelsTable,
    // which is a sibling in a different flex column, not a descendant here).
    // Its top has to be measured as the actual distance from that ancestor's
    // top edge to ChannelsTable's bottom edge - anything else (a flat
    // constant, or measuring ChannelsTable's own height in isolation) drifts
    // as soon as something with variable height (the pending-settings
    // banner, a unit-mismatch warning, etc.) changes how far down
    // ChannelsTable actually starts.
    const updatePosition = () => {
      const channelsTableElement = document.getElementById('channels-table-container');
      const cycleSettingsElement = channelsTableElement?.closest('.c-cycle-settings');

      if (channelsTableElement && cycleSettingsElement) {
        const channelsRect = channelsTableElement.getBoundingClientRect();
        const containerRect = cycleSettingsElement.getBoundingClientRect();
        setDeviceStepsTop(channelsRect.bottom - containerRect.top);
        setDeviceStepsWidth(channelsRect.width);
      }
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    // Observe the whole .c-cycle-settings subtree, not just
    // #channels-table-container - the banner and other conditional content
    // that shifts ChannelsTable's position live outside the table container
    // itself, so a narrower observer would miss exactly the changes that
    // matter here.
    const channelsTableElement = document.getElementById('channels-table-container');
    const cycleSettingsElement = channelsTableElement?.closest('.c-cycle-settings');
    const observer = new MutationObserver(updatePosition);
    if (cycleSettingsElement) {
      observer.observe(cycleSettingsElement, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      observer.disconnect();
    };
  }, []);
  const [initialSettings, setInitialSettings] = useState<IRecipeEditable>(null);
  const [managedSteps, setManagedSteps] = useState<IRecipeStepEditable[]>([]);
  const [recipeError, setRecipeError] = useState<string | null>(null);
  const [recipeSteps, setRecipeSteps] = useState<IPostWeldRecipeStep[]>([]);
  const lastSettingsRef = useRef<IPostWeldSettings | null>(null);
  const lastCycleDbSettingsRef = useRef<IDbCycleSettings | null>(null);
  const appliedDbSettingsVersion = useRef<string | null>(null);

  if (settings) {
    lastSettingsRef.current = settings;
  }

  if (cycleDbSettings) {
    lastCycleDbSettingsRef.current = cycleDbSettings;
  }

  const effectiveSettings = settings ?? lastSettingsRef.current;
  const effectiveCycleDbSettings = cycleDbSettings ?? lastCycleDbSettingsRef.current;

  const { t } = useTranslation();

  const convertRecipeSettingsForDisplay = (recipe: IRecipeEditable): IRecipeEditable => {
    if (!recipe || measurement === CycleMeasurement.Celsius) {
      return recipe;
    }

    return {
      ...recipe,
      rateUpperTolerance: convertTemperatureDelta(
        recipe.rateUpperTolerance,
        CycleMeasurement.Celsius,
        measurement,
      ) as number,
      rateLowerTolerance: convertTemperatureDelta(
        recipe.rateLowerTolerance,
        CycleMeasurement.Celsius,
        measurement,
      ) as number,
      holdUpperTolerance: convertTemperatureDelta(
        recipe.holdUpperTolerance,
        CycleMeasurement.Celsius,
        measurement,
      ) as number,
      holdLowerTolerance: convertTemperatureDelta(
        recipe.holdLowerTolerance,
        CycleMeasurement.Celsius,
        measurement,
      ) as number,
    };
  };

  const convertRecipeSettingsForApi = (recipe: IRecipeEditable): IRecipeEditable => {
    if (!recipe || measurement === CycleMeasurement.Celsius) {
      return recipe;
    }

    return {
      ...recipe,
      rateUpperTolerance: convertTemperatureDelta(
        recipe.rateUpperTolerance,
        measurement,
        CycleMeasurement.Celsius,
      ) as number,
      rateLowerTolerance: convertTemperatureDelta(
        recipe.rateLowerTolerance,
        measurement,
        CycleMeasurement.Celsius,
      ) as number,
      holdUpperTolerance: convertTemperatureDelta(
        recipe.holdUpperTolerance,
        measurement,
        CycleMeasurement.Celsius,
      ) as number,
      holdLowerTolerance: convertTemperatureDelta(
        recipe.holdLowerTolerance,
        measurement,
        CycleMeasurement.Celsius,
      ) as number,
    };
  };

  const convertRecipeFieldValueForApi = (key: string, value?: string) => {
    const isTemperatureField = [
      'rateUpperTolerance',
      'rateLowerTolerance',
      'holdUpperTolerance',
      'holdLowerTolerance',
    ].includes(key);

    if (!isTemperatureField || measurement === CycleMeasurement.Celsius) {
      return value;
    }

    return String(convertTemperatureDelta(Number(value), measurement, CycleMeasurement.Celsius));
  };

  const convertStepForDisplay = (step: IRecipeStepEditable): IRecipeStepEditable => {
    const sourceMeasurement = getStepMeasurement(step);

    if (step.mode !== StepMode.Rate) {
      return {
        ...step,
        isCelsius: measurement === CycleMeasurement.Celsius,
        units: measurement,
      };
    }

    return {
      ...step,
      heatingRate: convertTemperatureDelta(step.heatingRate, sourceMeasurement, measurement) as number,
      heatingLimit: convertAbsoluteTemperature(step.heatingLimit, sourceMeasurement, measurement) as number,
      isCelsius: measurement === CycleMeasurement.Celsius,
      units: measurement,
    };
  };

  const onUpdateSettingsHandler = async (action: CycleSettingAction, value?: string) => {
    const payload = { action };
    if (value) {
      payload['value'] = value;
    }
    await updateSettings({
      id: cycleId,
      payload: payload,
    });
  };

  const getRecipeSteps = (settings: IPostWeldSettings) => {
    if (!settings) {
      return [];
    }

    return settings.steps.slice(0, settings.numberOfSteps.value);
  };

  //used only on first load
  const isUpdatedSteps = useRef(false);

  const fetchData = async () => {
    if (!effectiveSettings || !effectiveCycleDbSettings || effectiveSettings.mode.value !== CycleSettings.PostWeld) {
      return;
    }
    const postWeldSettings: IPostWeldSettings = effectiveSettings as unknown as IPostWeldSettings;
    const recSteps = getRecipeSteps(postWeldSettings);
    setRecipeSteps(recSteps);

    const dbSettingsVersion = `${effectiveCycleDbSettings.id}-${effectiveCycleDbSettings.updated_at}-${measurement}`;
    if (!isUpdatedSteps.current || appliedDbSettingsVersion.current !== dbSettingsVersion) {
      const stepsToSet = effectiveCycleDbSettings.steps.map((step: IRecipeStepEditable) => {
        const time = {
          days: null,
          hours: null,
          minutes: null,
        };

        if (step?.duration) {
          const parsedTime = parseMinutes(step.duration);
          time.days = parsedTime.days;
          time.hours = parsedTime.hours;
          time.minutes = parsedTime.minutes;
        } else {
          time.days = step.durationDays || 0;
          time.hours = step.durationHours || 0;
          time.minutes = step.durationMinutes || 0;
        }

        return {
          ...step,
          durationDays: time.days,
          durationHours: time.hours,
          durationMinutes: time.minutes,
        };
      });
      setInitialSettings(convertRecipeSettingsForDisplay(convertRecipeToPostWeldSettings(effectiveCycleDbSettings)));

      setManagedSteps(stepsToSet.map(convertStepForDisplay));
      isUpdatedSteps.current = true;
      appliedDbSettingsVersion.current = dbSettingsVersion;
    }
  };

  useEffect(() => {
    fetchData();
  }, [settings, cycleDbSettings, measurement]);

  useEffect(() => {
    return () => {
      clearSettings();
      isUpdatedSteps.current = false;
      appliedDbSettingsVersion.current = null;
    };
  }, []);

  const onAddStepHandler = async (values: IRecipeStepEditable) => {
    const step: IRecipeStepEditable = { ...values, order: managedSteps.length };
    setManagedSteps((state) => [...state, step]);
  };

  const onRemoveStepHandler = async (idx: number) => {
    const newArr2 = managedSteps
      .filter((_, index) => index !== idx)
      .map((step, index) => ({ ...step, order: index }));

    setManagedSteps(newArr2);
  };

  const onChangeHandler = (idx: number, values: IRecipeStepEditable) => {
    const newArr = managedSteps.map((item, index) => {
      if (index === idx) {
        return values;
      }
      return item;
    });
    setManagedSteps(newArr);
  };

  const onUpdateStepsHandler = async (values: IRecipeStepEditable[]) => {
    const stringData = JSON.stringify({ steps: values });

    await updateDbSettings({
      id: cycleId,
      payload: { steps: values },
    });

    await onUpdateSettingsHandler(CycleSettingAction.SET_RECIPE_CHANGE_STEPS, stringData);
  };

  const moveUpHanlder = async (idx: number) => {
    const item = managedSteps[idx];
    const changedOrder = item.order - 1;
    const changedIndex = idx - 1;

    if (changedOrder < 0 || changedIndex < 0) {
      return;
    }

    const curItem = managedSteps[idx];
    const upItem = managedSteps[changedIndex];

    const { first, scnd } = swapItems(curItem, upItem);
    setManagedSteps(
      managedSteps.map((step, stepIndex) => {
        if (stepIndex === idx) {
          return scnd;
        }
        if (stepIndex === changedIndex) {
          return first;
        }
        return step;
      }),
    );
  };

  const moveDownHanlder = async (idx: number) => {
    const item = managedSteps[idx];
    const changedOrder = item.order + 1;
    const changedIndex = idx + 1;

    if (changedOrder > stepsLimitCount - 1 || changedIndex >= managedSteps.length) {
      return;
    }

    const curItem = managedSteps[idx];
    const downItem = managedSteps[changedIndex];
    const { first, scnd } = swapItems(curItem, downItem);
    setManagedSteps(
      managedSteps.map((step, stepIndex) => {
        if (stepIndex === idx) {
          return scnd;
        }
        if (stepIndex === changedIndex) {
          return first;
        }
        return step;
      }),
    );
  };

  const onSelectDbRecipe = async (recipeId: string) => {
    const recipe = await getDbRecipe(recipeId);
    const recipeStepsFromDb = await getDbRecipeSteps(recipeId);
    if (recipe.mode === CycleSettings.PostWeld) setInitialSettings(convertRecipeSettingsForDisplay(recipe));
    //database recipe has order 1-10
    //should be converted to order 0-9
    setManagedSteps(
      recipeStepsFromDb.map((item, idx) => {
        return convertStepForDisplay({ ...item, order: idx });
      }),
    );
  };

  const onSaveRecipeIntoDb = async () => {
    try {
      await createPostWeldRecipeValidationSchema(measurement).validate(initialSettings, { abortEarly: false });
      const dbSteps = managedSteps.map((item, idx) =>
        item.units === CycleMeasurement.Celsius
          ? { ...item, isCelsius: true, order: idx + 1 }
          : { ...item, isCelsius: false, order: idx + 1 },
      );
      await createRecipeFromScratch({ ...convertRecipeSettingsForApi(initialSettings), steps: dbSteps });
    } catch (error) {
      if (error instanceof ValidationError) {
        const errorMessages = error.errors.join(', ');
        setRecipeError(errorMessages);
      }
    }
  };

  const onUpdateAllHandler = async () => {
    const apiInitialSettings = convertRecipeSettingsForApi(initialSettings);

    await updateDbSettings({
      id: cycleId,
      payload: { cycleDbSettings: { ...apiInitialSettings }, steps: managedSteps },
    });

    // As-entered (not Celsius-converted) values for the controller - see the
    // comment in onUpdateSettingField for why.
    const wholeRecipe = { ...initialSettings, steps: managedSteps };
    const stringData = JSON.stringify(wholeRecipe);
    await onUpdateSettingsHandler(CycleSettingAction.SET_CYCLE_UPDATE_WHOLE_RECIPE, stringData);
  };

  if (!initialSettings || !effectiveCycleDbSettings || !effectiveSettings) {
    return (
      <div className="c-pre-heat-settings h-full w-full">
        <div className=" flex h-full w-full items-center justify-center">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  const onUpdateSettingField = async ({ action, key, value }: IOnUpdateSettingField) => {
    if (key) {
      const data = {};
      data[key] = convertRecipeFieldValueForApi(key, value);
      await updateDbSettings({
        id: cycleId,
        payload: {
          cycleDbSettings: data,
        },
      });
    }

    // Send the raw, as-entered value (not the Celsius-converted one used for
    // storage) to the controller. The backend already knows the contract's
    // measurement and will only convert it if the controller's unit actually
    // differs - sending a pre-converted value here would force an
    // unnecessary, lossy round-trip through Celsius even when the contract
    // and controller already share a unit.
    await onUpdateSettingsHandler(action, value);
  };

  const onJumpPrevHandler = async () => {
    await onUpdateSettingsHandler(CycleSettingAction.SET_CYCLE_SKIP_TO_PREV_STEP);
  };

  const onJumpNextHandler = async () => {
    await onUpdateSettingsHandler(CycleSettingAction.SET_CYCLE_SKIP_TO_NEXT_STEP);
  };

  return (
    <div className="c-pre-heat-settings flex w-full flex-col items-start">
      <div className="w-full max-w-screen-lg">
        <div className="flex flex-row items-start justify-between gap-x-5 py-4">
          <p className="text-red">{recipeError}</p>
          <div className="flex w-full flex-auto justify-end gap-x-5">
            <ChooseRecipe onSelect={onSelectDbRecipe} />
            <InteractiveButton onClick={onSaveRecipeIntoDb}>{t('buttons.saveAsNewRecipe')}</InteractiveButton>
          </div>
        </div>
        {hasMixedContractControllerUnits && <UnitMismatchWarning />}

        <RecipeUpdateFields
          onUpdateSettings={onUpdateSettingField}
          settings={effectiveCycleDbSettings}
          initialSettings={initialSettings}
          setInitialSettings={setInitialSettings}
          measurement={measurement}
          controllerMeasurement={controllerMeasurement}
        />
      </div>

      <div
        className="absolute right-7 z-50  hidden pb-15 1.5xl:block"
        style={{ top: `${deviceStepsTop + 16}px`, width: `${deviceStepsWidth}px` }}
      >
        <DeviceStepsTable
          currentStepIdx={effectiveSettings.currentStep.value}
          stepMinutesLeft={effectiveSettings.minutesLeftInStep.value}
          deviceSteps={recipeSteps}
          channelState={effectiveSettings.state.value}
          jumpNextStep={onJumpNextHandler}
          jumpPrevStep={onJumpPrevHandler}
          measurement={measurement}
        />
      </div>

      <div className="mt-5 flex w-full flex-col justify-between gap-4 1.5xl:hidden">
        <DeviceStepsTable
          currentStepIdx={effectiveSettings.currentStep.value}
          stepMinutesLeft={effectiveSettings.minutesLeftInStep.value}
          deviceSteps={recipeSteps}
          channelState={effectiveSettings.state.value}
          jumpNextStep={onJumpNextHandler}
          jumpPrevStep={onJumpPrevHandler}
          measurement={measurement}
        />
      </div>

      <div className="mt-5 flex w-full flex-col justify-between gap-4">
        <ManagedStepsTable
          managedSteps={managedSteps}
          onRemoveStep={onRemoveStepHandler}
          onChangeStep={onChangeHandler}
          moveUpStep={moveUpHanlder}
          moveDownStep={moveDownHanlder}
          onPressUpdate={onUpdateStepsHandler}
          onCreateStep={onAddStepHandler}
        />
      </div>
      <div className="flex w-full justify-center py-10">
        <InteractiveButton onClick={onUpdateAllHandler} size={ElementSize.XL}>
          {t('buttons.updateAll')}
        </InteractiveButton>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  cycleDbSettings: state.cycles.cycleDbSettings,
  settings: state.cycles.cycle.settings as IPostWeldSettings,
  measurement: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
  controllerMeasurement: getControllerMeasurement(state.cycles.cycleIoTChannelsSettings),
  hasMixedContractControllerUnits:
    getHasMixedContractControllerUnits(
      state.cycles.cycle,
      getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
    ) ||
    hasMixedContractChannelUnits(
      state.cycles.cycleIoTChannelsSettings,
      getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
    ),
});

const mapDispatch = (dispatch: Dispatch) => ({
  // getSettings: dispatch.cycles.getSettingsByMode,
  clearSettings: dispatch.cycles.clearSettings,
  updateSettings: dispatch.cycles.updateSettings,
  updateDbSettings: dispatch.cycles.updateDbSettings,
  getDbRecipe: dispatch.recipes.getRecipe,
  getDbRecipeSteps: dispatch.recipes.getRecipeSteps,
  createRecipeFromScratch: dispatch.recipes.createRecipeFromScratch,
});

export const PostWeldSettings = connect<StateProps, DispatchProps, Props>(mapState, mapDispatch)(PostWeldSettingsView);
